import { useEffect, useRef } from "react";

/**
 * Runs `poll` every `intervalMs` while `enabled` is true and the tab is
 * visible. Polling pauses while the tab is hidden and resumes with an
 * immediate poll. The latest `poll` is always the one called, so callers
 * need not memoize it.
 */
export function useVisiblePoll(
  poll: () => void,
  intervalMs: number,
  enabled = true,
): void {
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let interval: ReturnType<typeof setInterval> | null = null;
    const tick = () => {
      pollRef.current();
    };
    const start = () => {
      if (interval === null) {
        interval = setInterval(tick, intervalMs);
      }
    };
    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      tick();
      start();
    };

    if (!document.hidden) {
      start();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stop();
    };
  }, [enabled, intervalMs]);
}
